import { Component, ComponentType } from 'react';
import { Redirect, RedirectProps, Route, RouteProps, Switch } from 'react-router';
import { BraxModals, BraxModalsType } from 'design-system';
import { CreateCustomModalNode } from 'design-system/dist/components/BraxModals/context';

import paymentInstallments, { PaymentInstallments } from '../variables/payment-installments';
import paymentMethod, { PaymentMethod } from '../variables/payment-method';
import paymentInterval, { PaymentInterval } from '../variables/payment-interval';
import { withContexts } from '../utils/with-context';
import queryString from '../utils/query-string';
import authRouter from './auth';
import usersRouter from './users';
import configurationsRouter from './configurations';
import projectsRouter from './projects';
import helpRouter from './help';
import coursesRouter from './courses';
import Checkout from '../pages/plans/Checkout';
import Plans from '../pages/plans/Plans';

type Extract<Object extends {}, Map extends Object> = Omit<Object, keyof Map> & Map;

type ModalConfig = Extract<Omit<RouteProps, 'component' | 'render' | 'children'>, {
    path: string,
    redirect: string,
    render: () => CreateCustomModalNode
}>;

type RouteConfig = Extract<RouteProps, {
    path: string,
    component: ComponentType<any>
}>;

type RedirectConfig = Extract<RedirectProps, { from: string, to: string }>;

export type AppRoute = RouteConfig | RedirectConfig | ModalConfig;

const authenticated: AppRoute[] = [
    ...usersRouter,
    ...configurationsRouter,
    ...projectsRouter,
    ...helpRouter,
    ...coursesRouter,
    {
        path: '/plans',
        redirect: '/projects',
        render: () => <Plans />
    }, {
        path: '/checkout',
        redirect: '/projects',
        render: () => {
            const options = queryString.getAll<{ plan: string, coupon: string, installments: string, interval: string, method: string }>(['plan', 'coupon', 'installments', 'interval', 'method']);

            const plan = options.plan ? Number(options.plan) : Number.NaN;

            const installments = options.installments ? Number(options.installments) : Number.NaN;

            return (
                <Checkout
                    plan={!Number.isNaN(plan) ? plan : undefined}
                    interval={paymentInterval.findIndex(option => option.value === options.interval) !== -1 ? options.interval as PaymentInterval : undefined}
                    method={paymentMethod.findIndex(option => option.value === options.method) !== -1 ? options.method as PaymentMethod : undefined}
                    installments={!Number.isNaN(installments) && paymentInstallments.findIndex(option => option.value === installments) !== -1 ? installments as PaymentInstallments : undefined}
                    coupon={options.coupon!}
                />
            );
        }
    }, {
        from: '*',
        to: '/projects'
    }
];

const unauthenticated: AppRoute[] = [
    ...authRouter,
    {
        from: '/',
        to: '/login',
        exact: true
    }, {
        from: '/:url*',
        to: '/login?redirect=/:url'
    }
];

const RenderModal = withContexts({
    modals: BraxModals
}, class extends Component<ModalConfig & { modals: BraxModalsType }, { redirect: boolean }> {

    state = { redirect: false };

    componentDidMount() {
        this.props.modals.custom(this.props.render());

        this.setState({ redirect: true });
    }

    render() {
        if (!this.state.redirect)
            return null;

        const { render, redirect, modals, ...route } = this.props;

        return (
            <Route {...route} render={() => <Redirect to={redirect} />} />
        );
    }

});

const renderRoutes = (routes: AppRoute[]) => (
    <Switch>
        { routes.map(route => 'redirect' in route ? (
            <RenderModal key={route.path} {...route} />
        ) : 'from' in route ? (
            <Redirect key={route.to} {...route} />
        ) : (
            <Route key={route.path} {...route} />
        )) }
    </Switch>
);

const routers = {
    authenticated,
    unauthenticated,
    rendered: {
        authenticated: renderRoutes(authenticated),
        unauthenticated: renderRoutes(unauthenticated)
    }
};

export default routers;
